<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { RouterLink } from 'vue-router';

import { eqsApi } from '@/api/eqs';
import { errorMessage } from '@/utils/format';

/**
 * 「这个项目还缺什么」。
 *
 * 18 个在管项目里有 BOM 的只有 2 个、有 PFMEA 的 3 个，2036 条交付物里
 * 2029 条没挂文件（2026-08-13 盘点）。能力早就有了，缺的是「有人知道自己
 * 项目缺哪块、点哪里能补」。
 *
 * 所以这张卡只讲三件事：差多少、缺了会怎样、去哪补。
 * 不做综合评分——一个 78 分的项目不告诉任何人下一步做什么。
 */
interface Gap {
  key: string;
  title: string;
  detail: string;
  consequence: string;
  severity: 'blocking' | 'important' | 'suggested';
  path: string;
  actionLabel: string;
}

const props = defineProps<{ projectId: string | number }>();

const gaps = ref<Gap[]>([]);
const complete = ref(false);
const loading = ref(true);
const error = ref('');

async function load(): Promise<void> {
  loading.value = true;
  error.value = '';
  try {
    const payload = await eqsApi.projectReadiness(props.projectId);
    gaps.value = (payload.gaps ?? []) as unknown as Gap[];
    complete.value = Boolean(payload.complete);
  } catch (caught) {
    gaps.value = [];
    error.value = errorMessage(caught);
  } finally {
    loading.value = false;
  }
}

onMounted(load);

const SEVERITY_LABEL: Readonly<Record<Gap['severity'], string>> = {
  blocking: '挡住流程',
  important: '要补',
  suggested: '建议补',
};
</script>

<template>
  <article class="content-panel readiness">
    <header class="section-heading">
      <div>
        <span>WHAT&#39;S MISSING</span>
        <h2>这个项目还缺什么</h2>
      </div>
      <b v-if="gaps.length" class="readiness-count">{{ gaps.length }} 项</b>
    </header>

    <p v-if="loading" class="readiness-hint">正在核对…</p>
    <p v-else-if="error" class="readiness-hint readiness-error">读取失败：{{ error }}</p>
    <p v-else-if="complete" class="readiness-hint readiness-ok">
      没有明显缺口：团队、责任人、日期、BOM、PFMEA、控制计划、交付物文件都齐了。
    </p>

    <ol v-else class="readiness-list">
      <li v-for="gap in gaps" :key="gap.key" :class="`is-${gap.severity}`">
        <div class="readiness-copy">
          <b>
            {{ gap.title }}
            <i class="readiness-tag">{{ SEVERITY_LABEL[gap.severity] }}</i>
          </b>
          <span class="readiness-detail">{{ gap.detail }}</span>
          <small>{{ gap.consequence }}</small>
        </div>
        <RouterLink class="button button-secondary readiness-action" :to="`/projects/${projectId}/${gap.path}`">
          {{ gap.actionLabel }} →
        </RouterLink>
      </li>
    </ol>
  </article>
</template>

<style scoped>
.readiness { border-top: 3px solid var(--blue); }
.readiness-count {
  padding: 3px 10px; border-radius: 999px;
  background: rgba(0, 117, 174, .1); color: var(--blue); font-size: 13px;
}
.readiness-hint { margin: 10px 0 0; color: var(--muted); font-size: 13px; line-height: 1.8; }
.readiness-error { color: var(--risk); }
.readiness-ok { color: var(--ok, #1f9a5c); }

.readiness-list { margin: 10px 0 0; padding: 0; list-style: none; display: grid; gap: 10px; }
.readiness-list li {
  display: flex; align-items: center; gap: 14px;
  padding: 11px 13px; border: 1px solid var(--line); border-left-width: 3px;
  border-radius: var(--radius-s); background: var(--white);
}
/* 三档只用颜色区分紧迫度，红色留给「真的挡住流程」 */
.is-blocking { border-left-color: var(--risk); }
.is-important { border-left-color: var(--warning-graphic, #e8a13a); }
.is-suggested { border-left-color: var(--line); }

.readiness-copy { flex: 1; min-width: 0; display: grid; gap: 3px; }
.readiness-copy b { display: flex; align-items: center; gap: 8px; font-size: 14px; color: var(--ink); }
.readiness-tag {
  padding: 1px 7px; border-radius: 999px; background: rgba(0, 0, 0, .05);
  color: var(--muted); font-size: 11px; font-style: normal; font-weight: 700;
}
.is-blocking .readiness-tag { background: rgba(209, 52, 56, .1); color: var(--risk); }
.readiness-detail { color: var(--ink); font-size: 13px; }
.readiness-copy small { color: var(--muted); font-size: 12px; line-height: 1.7; }
.readiness-action { flex-shrink: 0; white-space: nowrap; }

@media (max-width: 720px) {
  .readiness-list li { flex-direction: column; align-items: stretch; }
}
</style>
